import { useAuthStore } from '@/stores/auth.js';
import { isTokenExpired } from '@/utils/jwtUtils.js';

// 认证守卫 - 需要登录才能访问
export const authGuard = (to, from, next) => {
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    // 保存用户想要访问的页面
    next({
      path: '/auth',
      query: { redirect: to.fullPath }
    });
  } else {
    next();
  }
};

// 访客守卫 - 已登录用户不能访问（如登录、注册页面）
export const guestGuard = (to, from, next) => {
  const authStore = useAuthStore();
  
  if (authStore.isAuthenticated) {
    next('/');
  } else {
    next();
  }
};

// 角色守卫工厂函数
export const roleGuard = (allowedRoles) => {
  return (to, from, next) => {
    const authStore = useAuthStore();
    
    if (!authStore.isAuthenticated) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
      return;
    }
    
    if (authStore.hasPermission(allowedRoles)) {
      next();
    } else {
      // 权限不足，跳转到403页面或首页
      next('/403');
    }
  };
};

// 管理员守卫
export const adminGuard = roleGuard([1]); // ADMIN

// 老师守卫
export const teacherGuard = roleGuard([1, 2]); // ADMIN, TEACHER

// 学生守卫
export const studentGuard = roleGuard([1, 2, 3]); // ADMIN, TEACHER, STUDENT

// 全局前置守卫
export const setupGlobalGuards = (router) => {
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();

    // 兜底机制：如果已登录，主动验证本地 Token 是否过期
    if (authStore.isAuthenticated) {
      const token = localStorage.getItem('accessToken');
      if (token && isTokenExpired(token)) {
        console.warn('前端主动检测到 Token 已过期，自动退出登录');
        authStore.logout();
        return; // authStore.logout 内部会执行 window.location.href 跳转，因此不需要 next()
      }
    }

    // 初始化认证状态
    if (!authStore.user && authStore.isAuthenticated) {
      try {
        await authStore.fetchProfile();
      } catch (error) {
        console.error('获取用户信息失败:', error);
        // 检查是否是账户待审核错误
        if (error.response?.data?.code === 'ACCOUNT_PENDING' || error.code === 'ACCOUNT_PENDING') {
          next('/account-pending');
          return;
        }
        authStore.logout();
      }
    }

    // 检查用户状态，如果是待审核状态则重定向
    if (authStore.isAuthenticated && authStore.user?.status === 0 && to.path !== '/account-pending') {
      next('/account-pending');
      return;
    }

    // 如果访问个人信息页面，强制获取最新用户数据
    if (to.name === 'profile-settings' && authStore.isAuthenticated) {
      try {
        console.log('访问个人信息页面，强制获取最新用户数据');
        await authStore.fetchProfile(true); // 强制刷新
      } catch (error) {
        console.error('获取最新用户信息失败:', error);
      }
    }

    // 检查路由元信息中的权限要求
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next({
        path: '/auth',
        query: { redirect: to.fullPath }
      });
      return;
    }

    if (to.meta.requiresGuest && authStore.isAuthenticated) {
      next('/');
      return;
    }

    if (to.meta.requiresAdmin && !authStore.hasPermission([1])) {
      next('/403');
      return;
    }

    if (to.meta.roles && !authStore.hasPermission(to.meta.roles)) {
      next('/403');
      return;
    }

    next();
  });
  
  // 全局后置钩子
  router.afterEach((to, from) => {
    // 更新页面标题
    if (to.meta.title) {
      document.title = `${to.meta.title} - Diva AI`;
    } else {
      document.title = 'Diva AI - 智能音乐学习平台';
    }
    
    // 页面访问统计等
    console.log(`页面访问: ${from.path} -> ${to.path}`);
  });
};
